(() => {
  const configElement = document.getElementById('google-tag-manager-config');
  if (!configElement || window.crosleyGtmInitialized) return;
  const config = JSON.parse(configElement.textContent);
  window.crosleyGtmInitialized = true;
  const dataLayer = (window.dataLayer = window.dataLayer || []);
  let loaded = false;
  let permitted = false;
  let viewedProduct = false;
  let consentGeneration = 0;
  const checkoutForms = new WeakSet();

  function consentCommand() {
    dataLayer.push(arguments);
  }

  consentCommand('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });

  function processingAllowed() {
    const privacy = window.Shopify?.customerPrivacy;
    return privacy?.analyticsProcessingAllowed?.() === true && privacy?.marketingAllowed?.() === true;
  }

  function canTrack() {
    return permitted && processingAllowed();
  }

  function commerceEvent(event, items, currency = config.currency, callback) {
    if (!canTrack() || items.length === 0) return false;
    dataLayer.push({ ecommerce: null });
    dataLayer.push({
      event,
      ecommerce: {
        currency,
        value: Number(items.reduce((sum, item) => sum + item.price * item.quantity, 0).toFixed(2)),
        // This container's Meta tags read currency from ecommerce.items.0.currency.
        items: items.map((item) => ({ ...item, currency })),
      },
      ...(callback ? { eventCallback: callback, eventTimeout: 300 } : {}),
    });
    return true;
  }

  function ajaxItem(item, quantity = item.quantity) {
    return {
      item_id: item.sku || String(item.variant_id || item.id),
      item_name: item.product_title || item.title,
      item_brand: item.vendor,
      item_category: item.product_type,
      item_variant: item.variant_title || '',
      price: (item.final_price ?? item.price) / 100,
      quantity,
    };
  }

  function productView() {
    if (!config.product || viewedProduct || !canTrack()) return;
    const product = config.product;
    const selectedId = document.querySelector('product-form-component input[name="id"]')?.value;
    const variant = product.variants.find((item) => String(item.id) === selectedId) || product.variants[0];
    if (!variant) return;
    viewedProduct = commerceEvent('view_item', [{
      item_id: variant.sku || String(variant.id),
      item_name: product.title,
      item_brand: product.vendor,
      item_category: product.type,
      item_variant: variant.title,
      price: variant.price / 100,
      quantity: 1,
    }]);
  }

  function syncConsent() {
    const privacy = window.Shopify?.customerPrivacy;
    const analyticsAllowed = privacy?.analyticsProcessingAllowed?.() === true;
    const marketingAllowed = privacy?.marketingAllowed?.() === true;
    const advertisingDataAllowed = marketingAllowed && privacy?.saleOfDataAllowed?.() === true;
    // Google consent is shared with Shopify's native Google pixel. Analytics
    // permission must not be denied merely because marketing was declined.
    const allowed = processingAllowed();
    if (allowed !== permitted) consentGeneration += 1;
    permitted = allowed;
    consentCommand('consent', 'update', {
      analytics_storage: analyticsAllowed ? 'granted' : 'denied',
      ad_storage: marketingAllowed ? 'granted' : 'denied',
      ad_user_data: advertisingDataAllowed ? 'granted' : 'denied',
      ad_personalization: advertisingDataAllowed ? 'granted' : 'denied',
    });
    // This mixed container also runs non-Google tags. Keep its existing gate
    // until those tags have individual consent checks configured in GTM.
    if (!allowed) {
      // Removing a script cannot stop already-running third-party tags. A fresh
      // page honours the persisted Shopify decision and never loads the container.
      if (loaded) window.location.reload();
      return;
    }
    if (!loaded) {
      loaded = true;
      dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(config.containerId)}`;
      document.head.appendChild(script);
    }
    productView();
  }

  function initializePrivacy(attempt = 0) {
    if (typeof window.Shopify?.customerPrivacy?.analyticsProcessingAllowed === 'function') {
      syncConsent();
    } else if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (error) => {
        if (!error) syncConsent();
      });
    } else if (attempt < 20) {
      window.setTimeout(() => initializePrivacy(attempt + 1), 250);
    }
    // Missing/failed privacy API deliberately leaves tracking disabled.
  }

  document.addEventListener('visitorConsentCollected', syncConsent);
  window.addEventListener('pageshow', () => {
    if (window.Shopify?.customerPrivacy) syncConsent();
  });

  // The native event resolves only after the cart request completes. Failed
  // requests, quantity edits and removals must never produce add_to_cart.
  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (event.action !== 'add' || !event.promise || !canTrack()) return;
    const generation = consentGeneration;
    event.promise.then(({ cart, detail }) => {
      if (detail?.didError || !cart || generation !== consentGeneration || !canTrack()) return;
      const items = (event.lines || []).map((line) => {
        const variantId = String(line.merchandiseId).split('/').pop();
        const item = detail?.items?.find((entry) => String(entry.variant_id || entry.id) === variantId);
        return item && line.quantity > 0 ? ajaxItem(item, line.quantity) : null;
      }).filter(Boolean);
      commerceEvent('add_to_cart', items, cart.cost?.totalAmount?.currencyCode || config.currency);
    }).catch(() => {});
  });

  // Track the storefront checkout hand-off, without modifying Shopify checkout.
  // Bound the delay so blocked tags/network failures cannot prevent checkout.
  document.addEventListener('submit', (event) => {
    const form = event.target;
    const submitter = event.submitter;
    if (!(form instanceof HTMLFormElement) || submitter?.name !== 'checkout') return;
    if (checkoutForms.has(form)) { event.preventDefault(); return; }
    if (!canTrack() || event.defaultPrevented) return;
    event.preventDefault();
    checkoutForms.add(form);
    const generation = consentGeneration;
    let resumed = false;
    const resume = () => {
      if (resumed) return;
      resumed = true;
      // Native submit omits the clicked button. Preserve its checkout flag and
      // avoid redispatching submit through GTM's form interception a second time.
      const checkout = document.createElement('input');
      checkout.type = 'hidden';
      checkout.name = 'checkout';
      checkout.value = submitter.value || 'checkout';
      form.appendChild(checkout);
      HTMLFormElement.prototype.submit.call(form);
    };
    const timeout = window.setTimeout(resume, 1000);
    fetch(config.cartUrl, { signal: AbortSignal.timeout(650) })
      .then((response) => {
        if (!response.ok) throw new Error('Cart unavailable');
        return response.json();
      })
      .then((cart) => {
        if (resumed || generation !== consentGeneration) return;
        const items = cart.items.map((item) => ajaxItem(item));
        if (commerceEvent('begin_checkout', items, cart.currency, resume)) {
          window.setTimeout(resume, 300);
        } else {
          resume();
        }
      })
      .catch(resume)
      .finally(() => {
        if (resumed) window.clearTimeout(timeout);
      });
  });

  initializePrivacy();
})();
